/**
 * THE constants band — «Etichetta **valore**» pairs said once (brief part 1 §1 b, pkgC2; the page
 * templates of brief part 2, C8, `docs/adr/9032`). One band in the codebase: a table's hoisted
 * constant columns (`DataTable`, ADR 9028 `constantHoist`) and a template's scope (the screen, the
 * calculator, the drill header) draw the same thing.
 *
 * On `surface-constants`. A description list — each pair a term and its value to a screen reader,
 * with no punctuation assembled in code (G6) — separated by 1px `separator` rules (#d6d2c7 light,
 * #3a2b2b dark: `hairline` barely reads on the tinted band). The label is `ink-label` (#5b5550;
 * `ink-secondary` measures 4.46:1 on this band), the value at 600, a code or an id in the mono face.
 *
 * `edge="table"` sits directly above a table's header and carries the table's 2px ink top rule;
 * `edge="boxed"` (the templates) stands on its own inside a 1px hairline.
 *
 * `data-constant-columns` / `data-constant-column` are the band's markers, the ones
 * `table-testing.ts`'s `constantPair` reads.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ICUValues } from '../primitives';

export interface ConstantPair {
  readonly id: string;
  readonly labelKey: string;
  readonly labelValues?: ICUValues;
  /** The value, already resolved — a market's name, a date, a run id. */
  readonly value: ReactNode;
  /** Codes and ids are set in mono (brief part 1 §1). */
  readonly mono?: boolean;
}

export function ConstantsBand({ pairs, edge = 'boxed', id, labelKey }: {
  readonly pairs: readonly ConstantPair[];
  readonly edge?: 'table' | 'boxed';
  /** For the table's `aria-describedby`. */
  readonly id?: string;
  /** The band's accessible name; a boxed band says «what holds for every row» unless told otherwise. */
  readonly labelKey?: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (pairs.length === 0) return null;
  const name = labelKey ?? (edge === 'boxed' ? 'template.constants.label' : undefined);
  const frame = edge === 'table' ? 'border-ink-primary border-t-2' : 'border-hairline border';
  return (
    <dl
      id={id}
      data-tour="constants-band"
      aria-label={name === undefined ? undefined : t(name)}
      className={`bg-surface-constants ${frame} flex flex-wrap items-center text-[12px]`}
      data-constant-columns=""
    >
      {pairs.map((pair) => (
        <div
          key={pair.id}
          className="border-separator flex min-w-0 items-center gap-2 border-e px-3 py-2 last:border-e-0"
          data-constant-column={pair.id}
        >
          <dt className="text-ink-label">{t(pair.labelKey, pair.labelValues ?? {})}</dt>
          <dd className={`text-ink-primary font-semibold${pair.mono === true ? ' font-numeric' : ''}`}>{pair.value}</dd>
        </div>
      ))}
    </dl>
  );
}
